import AddCircleIcon from "@mui/icons-material/AddCircle";
import AppsIcon from "@mui/icons-material/Apps";
import {
  Button,
  Container,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { PageTitleBar } from "common/components";
import DetailViewLoading from "common/components/DetailViewLoading";
import GrassWedgeIcon from "common/icons/GrassWedgeIcon";
import { useTranslation } from "common/locales";
import { FieldsList } from "features/farmOverview";
import { GrassWedgeDialog } from "features/grassWedge";
import {
  getAllFieldsForAnimalGroupId,
  getAnimalGroupColor,
  getDemand,
  getDemandForFeedAllocations,
  getFieldGeomsForAnimalGroup,
  getGroupWeight,
  getTotalAnimalCount,
  selectAnimalGroupById,
  selectCurrentFarm,
  setZoomToGeom,
  useAppDispatch,
  useAppSelector,
} from "model";
import { useEffect, useState } from "react";
import { Link, useHistory, useParams, useRouteMatch } from "react-router-dom";
import styled from "styled-components";
import { mixpanel } from "../../common/analytics";
import AnimalGroupMenu from "./AnimalGroupMenu";
import AssignFieldDialog from "./AssignFieldDialog";

import { feedBudgetPlannerEnabledForUserAndAnimalGroup } from "common/featureToggle";
import {
  AnimalGroupColorIcon,
  AnimalGroupWeightIcon,
  GrassIcon,
} from "common/icons";
import { FeedAllocationDialog } from "features/feedAllocation";

export default function AnimalGroupDetail() {
  const [assignFieldDialogOpen, setAssignFieldDialogOpen] =
    useState<boolean>(false);
  const { animalGroupId } = useParams<{ animalGroupId: string }>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const history = useHistory();
  const currentFarm = useAppSelector(selectCurrentFarm);

  const animalGroup = useAppSelector((state) =>
    selectAnimalGroupById(state, animalGroupId)
  );

  const handleBackToFarm = () => {
    if (currentFarm?.guid) {
      history.push(`/farm/${currentFarm.guid}`);
    }
  };

  const animalCount = useAppSelector((state) =>
    getTotalAnimalCount(state, animalGroupId)
  );

  const groupWeight = useAppSelector((state) =>
    getGroupWeight(state, animalGroupId)
  );
  const animalGroupColor = useAppSelector(getAnimalGroupColor(animalGroupId)); // TODO: remove when fully migrated to the colors from the database

  const user = useAppSelector((state) => state.user.details);
  const feedBudgetPlannerEnabled =
    feedBudgetPlannerEnabledForUserAndAnimalGroup(user, animalGroup);

  const grassWedgeMatch = useRouteMatch(
    "/animal-group/:animalGroupId/grass-wedge"
  );
  const feedAllocationMatch = useRouteMatch(
    "/animal-group/:animalGroupId/feed-allocation"
  );
  const { t } = useTranslation();

  const fields = useAppSelector(getAllFieldsForAnimalGroupId(animalGroupId));

  const demand = useAppSelector((state) => getDemand(state, animalGroupId));
  const feedAllocationDemand = useAppSelector((state) =>
    getDemandForFeedAllocations(state, animalGroupId)
  );
  const totalAreaForGroup = fields.reduce((acc, f) => acc + f.area, 0);
  let demandPerHaAndDay = demand
    ? Math.round((demand / totalAreaForGroup) * 10000)
    : 0;

  if (feedBudgetPlannerEnabled) {
    demandPerHaAndDay = feedAllocationDemand
      ? Math.round((feedAllocationDemand / totalAreaForGroup) * 10000)
      : 0;
  }

  const showGrassWedgeButton = true; // always allow to open grasswedge

  useZoomToFieldGeoms(animalGroupId);

  if (!animalGroup) return <DetailViewLoading size={2} />;

  const { name } = animalGroup;

  return (
    <>
      <PageTitleBar
        title={"Animal Group"}
        onBack={handleBackToFarm}
        showDivider
      />
      <Container
        maxWidth="sm"
        sx={{
          pt: isMobile ? 2 : 0,
          pb: isMobile ? 10 : 2,
          paddingTop: isMobile
            ? "calc(env(safe-area-inset-top) + 16px)"
            : undefined,
        }}
      >
        <List>
          <ListItem>
            <ListItemIcon>
              <AnimalGroupColorIcon
                color={animalGroup.color ?? animalGroupColor}
              />
            </ListItemIcon>
            <ListItemText primary={name} />
            <ListItemSecondaryAction>
              <AnimalGroupMenu {...animalGroup} />
            </ListItemSecondaryAction>
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <AnimalGroupWeightIcon fontSize="medium" />
            </ListItemIcon>
            <ListItemText
              primary={`${groupWeight || ""} kg`}
              secondary={t("animalGroup.groupWeight")}
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <GrassIcon fontSize="medium" />
            </ListItemIcon>
            <ListItemText
              primary={
                fields.length === 0
                  ? t("animalGroup.noFieldsAssigned")
                  : t("animalGroup.demandUnit", { demandPerHaAndDay })
              }
              secondary={t("animalGroup.groupDemand")}
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <AppsIcon fontSize="medium" />
            </ListItemIcon>
            <ListItemText
              primary={animalCount}
              secondary={t("animalGroup.animalCount")}
            />
          </ListItem>
        </List>
      </Container>
      <Divider />
      <Container>
        <Grid
          container
          direction="column"
          justifyContent="center"
          alignItems="center"
        >
          <GrassWedgeButton
            animalGroupId={animalGroupId}
            disabled={!showGrassWedgeButton}
          />
          {showGrassWedgeButton && (
            <GrassWedgeDialog
              open={Boolean(grassWedgeMatch)}
              close={() => history.push(`/animal-group/${animalGroupId}`)}
              animalGroup={animalGroup}
            />
          )}
          <StyledButton
            startIcon={
              <AddCircleIcon
                sx={{ color: "#ffffff !important", fill: "#ffffff !important" }}
              />
            }
            variant="contained"
            color="primary"
            onClick={() => {
              mixpanel.track(
                "feed budgeting assign fields to this animal group button clicked"
              );
              setAssignFieldDialogOpen(true);
            }}
            sx={{
              padding: "8px 12px",
              "&:hover": {
                backgroundColor: "#2E7831",
              },
            }}
          >
            {t("animalGroup.assignField")}
          </StyledButton>
          <AssignFieldDialog
            open={assignFieldDialogOpen}
            close={() => setAssignFieldDialogOpen(false)}
            animalGroupId={animalGroupId}
          />
          {feedBudgetPlannerEnabled && (
            <FeedAllocationButton animalGroupId={animalGroupId} />
          )}
          {feedBudgetPlannerEnabled && (
            <FeedAllocationDialog
              open={Boolean(feedAllocationMatch)}
              close={() => history.push(`/animal-group/${animalGroupId}`)}
              animalGroup={animalGroup}
            />
          )}
        </Grid>
      </Container>
      <Container>
        <FieldsList fieldIds={fields.map((field) => field.guid)} />
      </Container>
    </>
  );
}

function useZoomToFieldGeoms(animalGroupId: string) {
  const dispatch = useAppDispatch();
  const fieldGeoms = useAppSelector(getFieldGeomsForAnimalGroup(animalGroupId));

  useEffect(() => {
    if (fieldGeoms.features.length > 0) {
      dispatch(setZoomToGeom(fieldGeoms));
    }
  }, [fieldGeoms, dispatch]);
}

function GrassWedgeButton({ animalGroupId, disabled }: GrassWedgeButtonProps) {
  const { t } = useTranslation();

  if (!disabled)
    return (
      <StyledButton
        startIcon={<GrassWedgeIcon />}
        variant="outlined"
        size="large"
        component={Link}
        to={`/animal-group/${animalGroupId}/grass-wedge`}
      >
        {t("grassWedge.open")}
      </StyledButton>
    );

  return (
    <Tooltip title={t("animalGroup.grassWedgeButton.tooltip")} arrow>
      <TooltipChild>
        <StyledButton
          startIcon={<GrassWedgeIcon disabled />}
          variant="outlined"
          disabled
          size="large"
        >
          {t("grassWedge.open")}
        </StyledButton>
      </TooltipChild>
    </Tooltip>
  );
}

type GrassWedgeButtonProps = {
  animalGroupId: string;
  disabled?: boolean;
};

const StyledButton = styled(Button)(
  ({ theme }) => `
  width: 100%;
  margin-top: ${theme.spacing(1)};
`
) as typeof Button;

const TooltipChild = styled.div`
  width: 100%;
`;

function FeedAllocationButton({ animalGroupId }: { animalGroupId: string }) {
  const { t } = useTranslation();
  return (
    <StyledButton
      variant="contained"
      color="primary"
      component={Link}
      onClick={() => {
        mixpanel.track("view feed allocation button clicked");
      }}
      to={`/animal-group/${animalGroupId}/feed-allocation`}
    >
      {t("animalGroup.viewFeedAllocation")}
    </StyledButton>
  );
}
